import { computed } from 'vue'
import { useMatchStore } from '@/stores/match.js'
import { useTranslationsStore } from '@/stores/translations.js'
import { MATCH_TYPES } from '@/constants/matchTypes.js'

export function usePlayerValidation(teamManagement) {
    const matchStore = useMatchStore()
    const { t } = useTranslationsStore()

    const { playMode, teamStats } = teamManagement

    const gameTypeSettings = {
        [MATCH_TYPES.X01]: {
            teamPlayAllowed: true,
            maxPlayers: 4,
            botsAllowed: true,
            requiresTeamModeForMultiplePlayers: true,
            maxSinglesPlayers: 2,
            minPlayers: 1,
        },
        [MATCH_TYPES.CRICKET]: {
            teamPlayAllowed: false,
            maxPlayers: 2,
            botsAllowed: false,
            requiresTeamModeForMultiplePlayers: true,
            maxSinglesPlayers: 2,
            minPlayers: 2,
        },
        [MATCH_TYPES.KILLER]: {
            teamPlayAllowed: false,
            maxPlayers: 4,
            botsAllowed: false,
            requiresTeamModeForMultiplePlayers: false,
            maxSinglesPlayers: 4,
            minPlayers: 2,
        },
        [MATCH_TYPES.CAMELRACE]: {
            teamPlayAllowed: true,
            maxPlayers: 4,
            botsAllowed: false,
            requiresTeamModeForMultiplePlayers: false,
            maxSinglesPlayers: 4,
            minPlayers: 2,
        },
        [MATCH_TYPES.SEGMENTS]: {
            teamPlayAllowed: false,
            maxPlayers: 4,
            botsAllowed: false,
            requiresTeamModeForMultiplePlayers: false,
            maxSinglesPlayers: 4,
            minPlayers: 1,
        },
    }

    const allPlayers = computed(() => {
        return matchStore.setup.profiles || []
    })

    const readyPlayers = computed(() => {
        return allPlayers.value.filter((profile) => profile.ready === true)
    })

    const currentGameTypeSettings = computed(() => {
        const fallback = {
            teamPlayAllowed: false,
            maxPlayers: 4,
            botsAllowed: false,
            requiresTeamModeForMultiplePlayers: false,
            maxSinglesPlayers: 2,
            minPlayers: 2,
        }
        const matchTypeId = matchStore.setup.matchtype_id
        if (!matchTypeId || !(matchTypeId in gameTypeSettings)) {
            return fallback
        }
        return gameTypeSettings[matchTypeId] || fallback
    })

    const requiresStrictPlayerCount = computed(() => {
        const matchTypeId = matchStore.setup.matchtype_id
        return matchTypeId === MATCH_TYPES.CRICKET
    })

    const minPlayers = computed(() => {
        const base = currentGameTypeSettings.value.minPlayers || 2
        // Online matches always need the host plus at least one joined
        // friend, even for game types (X01) that allow solo/vs-bot locally.
        return matchStore.isRemotePlay ? Math.max(base, 2) : base
    })

    /**
     * The effective party-size cap right now, the single source of truth every
     * component should use instead of re-deriving it. Online play never offers
     * team mode (the toggle is hidden - see PlayerSelectionSection.vue), so no
     * game type can actually organize more than its solo limit into a match
     * online, regardless of that game type's team-mode maxPlayers - only local
     * team mode unlocks the full maxPlayers cap. This also protects against a
     * stale persisted playMode of 'teams' left over from a previous local
     * match: the `!matchStore.isRemotePlay` guard wins regardless.
     */
    const effectiveMaxPlayers = computed(() => {
        if (!matchStore.isRemotePlay && playMode.value === 'teams') {
            return currentGameTypeSettings.value.maxPlayers
        }
        return currentGameTypeSettings.value.maxSinglesPlayers
    })

    const maxPlayersReached = computed(() => {
        return allPlayers.value.length >= effectiveMaxPlayers.value
    })

    const validateSinglesMode = () => {
        if (allPlayers.value.length < minPlayers.value) return false

        if (requiresStrictPlayerCount.value) {
            return readyPlayers.value.length === allPlayers.value.length
        }

        return readyPlayers.value.length >= 1
    }

    const validateTeamsMode = () => {
        const stats = teamStats.value

        if (stats.unassigned.count > 0) return false
        if (stats.team1.readyCount === 0 || stats.team2.readyCount === 0) return false
        if (stats.team1.readyCount !== stats.team2.readyCount) return false
        if (readyPlayers.value.length !== allPlayers.value.length) return false

        return stats.team1.readyCount >= 1 && stats.team2.readyCount >= 1
    }

    const canProceed = computed(() => {
        if (playMode.value === 'singles') {
            return validateSinglesMode()
        } else if (playMode.value === 'teams') {
            return validateTeamsMode()
        }
        return false
    })

    const getSinglesValidationMessage = () => {
        const currentCount = allPlayers.value.length
        const min = minPlayers.value

        if (currentCount < min) {
            const needed = min - currentCount
            return needed > 1
                ? t('player_selector.need_more_players_plural', { n: needed, min })
                : t('player_selector.need_more_players_singular', { n: needed, min })
        }

        if (requiresStrictPlayerCount.value) {
            const unreadyCount = allPlayers.value.length - readyPlayers.value.length
            if (unreadyCount > 0) {
                return unreadyCount > 1
                    ? t('player_selector.unready_players_plural', { n: unreadyCount })
                    : t('player_selector.unready_players_singular', { n: unreadyCount })
            }
        } else if (readyPlayers.value.length < 1) {
            return t('player_selector.at_least_one_must_accept')
        }

        return ''
    }

    const getTeamsValidationMessage = () => {
        const stats = teamStats.value

        if (stats.unassigned.count > 0) {
            return stats.unassigned.count > 1
                ? t('player_selector.unassigned_players_plural', { n: stats.unassigned.count })
                : t('player_selector.unassigned_players_singular', { n: stats.unassigned.count })
        }

        if (stats.team1.readyCount === 0) {
            return t('player_selector.team_needs_ready_player', { team: t('player_selector.team_1') })
        }

        if (stats.team2.readyCount === 0) {
            return t('player_selector.team_needs_ready_player', { team: t('player_selector.team_2') })
        }

        if (stats.team1.readyCount !== stats.team2.readyCount) {
            const difference = Math.abs(stats.team1.readyCount - stats.team2.readyCount)
            const largerTeam =
                stats.team1.readyCount > stats.team2.readyCount
                    ? t('player_selector.team_1')
                    : t('player_selector.team_2')
            const smallerTeam =
                stats.team1.readyCount < stats.team2.readyCount
                    ? t('player_selector.team_1')
                    : t('player_selector.team_2')
            return difference > 1
                ? t('player_selector.teams_unequal_plural', {
                      larger: largerTeam,
                      difference,
                      smaller: smallerTeam,
                  })
                : t('player_selector.teams_unequal_singular', {
                      larger: largerTeam,
                      difference,
                      smaller: smallerTeam,
                  })
        }

        const unreadyCount = allPlayers.value.length - readyPlayers.value.length
        if (unreadyCount > 0) {
            return unreadyCount > 1
                ? t('player_selector.unready_players_plural', { n: unreadyCount })
                : t('player_selector.unready_players_singular', { n: unreadyCount })
        }

        return ''
    }

    const currentValidationMessage = computed(() => {
        if (playMode.value === 'singles') {
            return getSinglesValidationMessage()
        } else if (playMode.value === 'teams') {
            return getTeamsValidationMessage()
        }
        return ''
    })

    const canAddMorePlayers = computed(() => {
        return !maxPlayersReached.value
    })

    const requiresTeamModeForMorePlayers = computed(() => {
        if (!currentGameTypeSettings.value.requiresTeamModeForMultiplePlayers) {
            return false
        }
        return (
            playMode.value !== 'teams' &&
            allPlayers.value.length >= currentGameTypeSettings.value.maxSinglesPlayers
        )
    })

    const getMaxPlayersMessage = () => {
        return t('player_selector.max_players_for_game_type', {
            max: currentGameTypeSettings.value.maxPlayers,
        })
    }

    const getTeamModeRequiredMessage = () => {
        return t('player_selector.team_mode_required_for_more_players', {
            max: currentGameTypeSettings.value.maxSinglesPlayers,
        })
    }

    const canSwitchToSinglesMode = computed(() => {
        if (allPlayers.value.length > currentGameTypeSettings.value.maxSinglesPlayers) {
            return false
        }

        return true
    })

    const getSinglesModeBlockedMessage = () => {
        return t('player_selector.singles_mode_blocked_message', {
            max: currentGameTypeSettings.value.maxSinglesPlayers,
        })
    }

    return {
        allPlayers,
        readyPlayers,
        minPlayers,
        canProceed,
        currentValidationMessage,
        validateSinglesMode,
        validateTeamsMode,
        currentGameTypeSettings,
        effectiveMaxPlayers,
        maxPlayersReached,
        canAddMorePlayers,
        requiresTeamModeForMorePlayers,
        getMaxPlayersMessage,
        getTeamModeRequiredMessage,
        canSwitchToSinglesMode,
        getSinglesModeBlockedMessage,
    }
}
